import {
  BREAKPOINT_MOBILE,
  BREAKPOINT_TABLET,
  BreakpointConfig,
  ResponsivenessView,
  ScaleDownConfig,
} from '@/game/GameComponents/PhaserGame/Types/ManagerTypes/ResponsivenessManagerTypes';
import { SizedObject } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';

export class ResponsivenessManager {
  private static scaleDownHandlers = new WeakMap<SizedObject, () => void>();

  static InitializeResponsiveBreakpoints(config: BreakpointConfig) {
    const { scene, desktopView, tabletView, mobileView } = config;

    function updateViews() {
      const height = scene.game.scale.displaySize.height;
      const width = scene.scale.displaySize.width;
      const minDimension = Math.min(width, height);

      // Default hide everything.
      desktopView.setVisible(false);
      tabletView?.setVisible(false);
      mobileView?.setVisible(false);

      // Desktop breakpoint.
      if (minDimension > BREAKPOINT_TABLET) {
        desktopView.setVisible(true);
      }

      // Tablet view setup.
      if (tabletView) {
        if (
          minDimension <= BREAKPOINT_TABLET &&
          minDimension > BREAKPOINT_MOBILE
        ) {
          tabletView.setVisible(true);
        }
      } else {
        desktopView.setVisible(true);
      }

      // Mobile view setup.
      if (mobileView) {
        if (minDimension <= BREAKPOINT_MOBILE) {
          desktopView.setVisible(false);
          tabletView?.setVisible(false);
          mobileView.setVisible(true);
        }
      }
    }

    scene.scale.on('resize', updateViews);
    updateViews();
  }

  static IsMobileDevice(scene: Phaser.Scene, checkTouch?: boolean): boolean {
    // Option 1: Screen dimensions
    const height = scene.game.scale.displaySize.height;
    const width = scene.scale.displaySize.width;
    const minDimension = Math.min(width, height);
    const isSmallScreen = minDimension <= BREAKPOINT_MOBILE;

    // Option 3: Both (more restrictive)
    if (checkTouch) {
      // Option 2: Touch capability
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      return isSmallScreen && isTouch;
    }
    return isSmallScreen;
  }

  static GetDeviceOrientation(scene: Phaser.Scene) {
    const height = scene.game.scale.displaySize.height;
    const width = scene.scale.displaySize.width;
    return height > width ? 'portrait' : 'landscape';
  }

  static GetSuffixForView(view: ResponsivenessView) {
    switch (view) {
      case 'desktop':
      default:
        return '';
      case 'tablet':
        return '_t';
      case 'mobile':
        return '_m';
    }
  }

  static InitializeResizeHandler(
    scene: Phaser.Scene,
    callback: () => void,
    context: any,
  ) {
    scene.scale.on('resize', callback, context);
    scene.scale.on('orientationchanged', callback, context);

    // Fire the resize handler.
    callback();

    // Clean up when the scene is destroyed.
    scene.events.once('destroy', () => {
      scene.scale.off('resize', callback, context);
      scene.scale.off('orientationchanged', callback, context);
    });
  }

  /*
    Generic way to get a responsive component from a scene.
  */
  static GetResponsiveComponentFromScene<T extends Phaser.Scene>(
    scene: T,
    componentKey: keyof T,
    view: ResponsivenessView,
  ): any {
    const modifiedKeyForView =
      componentKey.toString() + ResponsivenessManager.GetSuffixForView(view);

    if (!(modifiedKeyForView in scene)) {
      return null;
    }
    return scene[modifiedKeyForView as keyof T];
  }

  /*
    Generic way to get a responsive component from a container.
  */
  static GetResponsiveComponentFromContainer<
    T extends Phaser.GameObjects.Container,
  >(container: T, componentKey: keyof T, view: ResponsivenessView): any {
    const modifiedKeyForView =
      componentKey.toString() + ResponsivenessManager.GetSuffixForView(view);

    if (!(modifiedKeyForView in container)) {
      return null;
    }
    return container[modifiedKeyForView as keyof T];
  }

  static GetAllResponsiveComponentsFromScene<T extends Phaser.Scene>(
    scene: T,
    componentKey: keyof T,
  ): any {
    return ['desktop', 'tablet', 'mobile']
      .map((view: string) => {
        return this.GetResponsiveComponentFromScene<T>(
          scene,
          componentKey,
          view as ResponsivenessView,
        );
      })
      .filter((result: any) => result);
  }

  static GetAllResponsiveComponentsFromContainer<
    T extends Phaser.GameObjects.Container,
  >(container: T, componentKey: keyof T): any {
    return ['desktop', 'tablet', 'mobile']
      .map((view: string) => {
        return this.GetResponsiveComponentFromContainer<T>(
          container,
          componentKey,
          view as ResponsivenessView,
        );
      })
      .filter((result: any) => result);
  }

  /*
    Returns desktop, tablet or mobile, not caring about specific UI scenes.
  */
  static GetCurrentGlobalViewForGame(scene: Phaser.Scene) {
    const height = scene.scale.displaySize.height;
    const width = scene.scale.displaySize.width;
    const minDimension = Math.min(width, height);

    if (minDimension > BREAKPOINT_TABLET) {
      return 'desktop';
    }

    if (minDimension <= BREAKPOINT_TABLET && minDimension > BREAKPOINT_MOBILE) {
      return 'tablet';
    }

    if (minDimension <= BREAKPOINT_MOBILE) {
      return 'mobile';
    }
    return 'desktop';
  }

  static MakeElementsScaleDownWithGameSize(
    elements: SizedObject[],
    config?: ScaleDownConfig,
  ) {
    elements.forEach((element) => {
      ResponsivenessManager.MakeElementScaleDownWithGameSize(element, config);
    });
  }

  static MakeElementScaleDownWithGameSize(
    element: SizedObject,
    config?: ScaleDownConfig,
  ) {
    const defaultConfig = {
      scaleModifier: 1,
      breakpoints: {
        width: 667,
        height: 375,
      },
      accountForWidth: true,
      accountForHeight: true,
    };
    config = { ...defaultConfig, ...config };

    const game = element.scene.game;
    const { breakpoints, accountForWidth, accountForHeight } = config;

    const resizeHandler = () => {
      const breakpointsWithDefaults = {
        width: breakpoints?.width || 860,
        height: breakpoints?.height || 375,
      };
      const gameHeight = game.scale.gameSize.height;
      const gameWidth = game.scale.gameSize.width;
      const minSize = Math.min(
        accountForWidth ? gameWidth : Number.MAX_SAFE_INTEGER,
        accountForHeight ? gameHeight : Number.MAX_SAFE_INTEGER,
      );
      const scaleToUse =
        minSize === gameHeight
          ? breakpointsWithDefaults.height
          : breakpointsWithDefaults.width;

      let ratio = 1;

      if (minSize <= scaleToUse) {
        ratio = minSize / scaleToUse;
      }

      element.scale = ratio * (config.scaleModifier ?? 1);
    };

    // Remove existing resize handlers.
    const existingHandler = this.scaleDownHandlers.get(element);
    if (existingHandler) {
      game.scale.off('resize', existingHandler);
      ResponsivenessManager.scaleDownHandlers.delete(element);
    }
    game.scale.on('resize', resizeHandler);
    resizeHandler();
    element.on('destroy', () => {
      game.scale.off('resize', resizeHandler);
    });
  }
}
